import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import { Calendar, Edit, Trash2, XCircle, CheckCircle } from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import apiTimezoneDirectory from "./api-timezone-directory";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import { fieldsConfig } from "./features/fields";
import { validation } from "./features/validation";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import ConfirmDialogue from "components/ui/ConfirmDialogue";

const SORT_OPTIONS = [
  { value: "created_date_desc", label: "Newest first" },
  { value: "created_date_asc", label: "Oldest first" },
];

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const Timezone = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "created_date",
    direction: "desc",
  });
  const [sortValue, setSortValue] = useState("created_date_desc");
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchTimezones = async () => {
    setLoading(true);
    try {
      const payload = {
        searchText: searchText,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
      };

      const response = await apiTimezoneDirectory.getAllTimezone(payload);

      if (response.data.status) {
        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
        onTotalCountChange?.(response.data.total_count || 0);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      console.error("Error fetching timezones:", error);
      if (error.response) {
        showAlert({ type: "error", message: error.response?.data?.message });
      } else {
        showAlert({ type: "error", message: "An unexpected error occurred. Please try again!" });
      }
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (options) => {
    return new Promise((resolve) => {
      setConfirmData({
        ...options,
        isOpen: true,
        resolve,
      });
    });
  };

  const handleConfirmClose = (result) => {
    if (confirmData.resolve) {
      confirmData.resolve(result);
    }
    setConfirmData((prev) => ({
      ...prev,
      isOpen: false,
      resolve: null,
    }));
  };

  const handleDeleteSelected = async (ids = []) => {
    if (ids.length === 0) {
      showAlert({ type: "warning", message: "Please select at least one timezone to delete." });
      return;
    }
    const confirmed = await openConfirm({
      title: "Delete Timezone",
      message:
        ids.length > 1
          ? `Are you sure you want to delete ${ids.length} selected timezones? This action cannot be undone.`
          : "Are you sure you want to delete this timezone? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiTimezoneDirectory.delete(ids);

      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message });
        fetchTimezones();
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      if (error.response) {
        showAlert({ type: "error", message: error.response?.data?.message });
      } else {
        showAlert({ type: "error", message: "An unexpected error occurred. Please try again!" });
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setResetSelectionTrigger((prev) => prev + 1);
  };

  const handleSortChange = (value) => {
    setSortValue(value);
    setSortConfig({ key: "created_date", direction: value.endsWith("_desc") ? "desc" : "asc" });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiTimezoneDirectory.getAllTimezone({
        searchText,
        skip: 0,
        limit: totalCount || 1000,
      });
      const rows = response.data?.data || [];

      if (rows.length === 0) {
        showAlert({ type: "warning", message: "There is no data to export." });
        return;
      }

      const header = ["IANA Name", "Display Name", "UTC Offset", "Status", "Created Date", "Last Update"];
      const lines = rows.map((row) =>
        [row.iana_name, row.display_name, row.utc_offset, row.is_activated ? "Active" : "Inactive", row.created_date, row.updated_date]
          .map(csvCell)
          .join(",")
      );
      const csv = [header.map(csvCell).join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "timezones.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Failed to export data" });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchTimezones();
  }, [currentPage, pageSize, searchText]);

  useEffect(() => {
    const sorted = sortData(data, sortConfig);
    setFilteredData(sorted);
  }, [data, sortConfig]);

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCreate = () => {
    setSelectedTimezone(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    const dataToEdit = data.find((row) => row.id === id);
    if (dataToEdit) {
      setSelectedTimezone(dataToEdit);
      setIsModalOpen(true);
    }
  };

  const handleToggleStatus = async (row) => {
    const confirmed = await openConfirm({
      title: `${row.is_activated ? "Deactivate" : "Activate"} Timezone`,
      message: `Are you sure you want to ${row.is_activated ? "deactivate" : "activate"} this timezone?`,
      confirmText: "Save Changes",
      cancelText: "Cancel",
      icon: <CheckCircle size={24} className="text-success" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiTimezoneDirectory.updateStatus({
        id: row.id,
        is_activated: !row.is_activated,
      });

      if (response.data.status) {
        showAlert({
          type: "success",
          message: `Timezone ${row.is_activated ? "deactivated" : "activated"} successfully`,
        });
        fetchTimezones();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleRefresh = () => {
    setSearchText("");
    setCurrentPage(1);
    setResetTrigger((prev) => prev + 1);
    fetchTimezones();
  };

  const handleModalSuccess = () => {
    fetchTimezones();
    setSelectedRows([]);
  };

  const columns = [
    {
      key: "iana_name",
      label: "IANA Name",
      width: "220px",
      sortable: true,
    },
    {
      key: "display_name",
      label: "Display Name",
      width: "220px",
      sortable: true,
    },
    {
      key: "utc_offset",
      label: "UTC Offset",
      width: "120px",
    },
    {
      key: "abbreviation",
      label: "Abbreviation",
      width: "120px",
    },
    {
      key: "is_activated",
      label: "Status",
      width: "140px",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            row.is_activated
              ? "bg-success_bg text-success border border-success_border"
              : "bg-danger_bg text-danger border border-danger_border"
          }`}
        >
          {row.is_activated ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "created_date",
      label: "Created Date",
      width: "140px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.created_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
    {
      key: "updated_date",
      label: "Last Update",
      width: "140px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.updated_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-1.5 justify-end">
      <button
        className="p-1.5 rounded-lg bg-info_bg text-info hover:bg-info_bg transition-all duration-200"
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row.id);
        }}
        title="Edit Timezone"
      >
        <Edit size={16} />
      </button>
      <button
        className="p-1.5 rounded-lg bg-danger_bg text-danger hover:bg-danger_bg transition-all duration-200"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete Timezone"
      >
        <Trash2 size={16} />
      </button>
      <button
        className={`p-1.5 rounded-lg transition-all duration-200 ${
          row.is_activated
            ? "bg-danger_bg text-danger hover:bg-danger_bg"
            : "bg-success_bg text-success hover:bg-success_bg"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Active - Click to Deactivate" : "Inactive - Click to Activate"}
      >
        {row.is_activated ? (
          <XCircle size={18} strokeWidth={2.5} />
        ) : (
          <CheckCircle size={18} strokeWidth={2.5} />
        )}
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
      <div className="flex flex-col h-full gap-4">
        <TableToolBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onCreate={handleCreate}
          onDeleteSelected={() => handleDeleteSelected(selectedRows)}
          selectedCount={selectedRows.length}
          resetSearchTrigger={resetTrigger}
          resultCount={totalCount}
          sortOptions={SORT_OPTIONS}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          onClearSelection={handleClearSelection}
          onExport={handleExport}
          exporting={exporting}
        />

        {data?.length === 0 && !loading ? (
          <div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
            <NoDataState
              icon="search"
              title="No Timezone Found"
              message="There is no timezone available!"
              showRefresh={false}
              showBack={false}
              showCreate={true}
              createText="Create New Timezone"
              onCreate={handleCreate}
              size="medium"
            />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden border rounded-lg border-border">
              <TableGrid
                data={filteredData}
                totalCount={totalCount}
                loading={loading}
                currentPage={currentPage}
                columns={columns}
                sortConfig={sortConfig}
                onSort={handleSort}
                onSelectionChange={setSelectedRows}
                pageSize={pageSize}
                selectable={true}
                renderActions={renderRowActions}
                resetSelectionTrigger={resetSelectionTrigger}
              />
            </div>

            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />

            {confirmData.isOpen && (
              <ConfirmDialogue
                confirmData={confirmData}
                handleConfirmClose={handleConfirmClose}
              />
            )}
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        id={selectedTimezone?.id}
        title="Timezone"
        fetchById={apiTimezoneDirectory.getById}
        createFn={apiTimezoneDirectory.create}
        updateFn={apiTimezoneDirectory.update}
        fields={fieldsConfig}
        validationSchema={validation}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Timezone;
